<template>
  <div>
    <h1 class="text-4xl font-bold mb-8">👨‍💼 Panel Administrativo</h1>

    <!-- Acciones Rápidas -->
    <div class="mb-8 flex gap-4 items-center justify-between">
      <div class="flex gap-4">
        <button
          @click="showCreateModal = true"
          class="btn-primary"
        >
          + Crear Nuevo Torneo
        </button>
        <router-link
          to="/admin/jueces"
          class="btn-secondary"
        >
          👨‍⚖️ Gestionar Jueces
        </router-link>
      </div>
      <button
        @click="cerrarSesionAdmin"
        class="btn-danger text-sm px-4 py-2"
      >
        🚪 Cerrar Sesión
      </button>
    </div>

    <!-- Modal Crear Torneo -->
    <Modal
      v-model="showCreateModal"
      title="Crear Nuevo Torneo"
      @confirm="crearTorneo"
    >
      <Input
        v-model="form.nombre"
        label="Nombre del Torneo"
        placeholder="Ej: Nacional 2024"
        required
      />
      <Input
        v-model="form.fecha"
        label="Fecha"
        type="date"
        required
      />
      <div class="mb-4">
        <label class="block text-sm font-semibold mb-2 text-black">
          Categorías <span class="text-red-600">*</span>
        </label>
        <div class="bg-gray-50 p-4 rounded-lg max-h-64 overflow-y-auto">
          <div
            v-for="cat in categorias"
            :key="cat.id"
            class="flex items-center mb-3"
          >
            <input
              type="checkbox"
              :id="`cat-${cat.id}`"
              :value="cat.id"
              v-model="form.categorias"
              class="mr-3"
            />
            <label :for="`cat-${cat.id}`" class="cursor-pointer text-black">
              {{ cat.nombre }} ({{ cat.total_competidores }} competidores)
            </label>
          </div>
        </div>
      </div>
    </Modal>

    <!-- Loading -->
    <Loading v-if="loading" message="Cargando torneos..." />

    <!-- Torneos -->
    <div v-else class="grid grid-cols-1 gap-4">
      <div v-if="torneos.length === 0" class="text-center py-12">
        <p class="text-gray-500 text-lg">No hay torneos creados. Crea uno para empezar.</p>
      </div>

      <div
        v-for="torneo in torneos"
        :key="torneo.id"
        class="card p-6"
      >
        <div class="flex justify-between items-start mb-4">
          <div>
            <h2 class="text-2xl font-bold mb-2">{{ torneo.nombre }}</h2>
            <p class="text-gray-600">
              📅 {{ formatDate(torneo.fecha) }} | Estado: <span class="font-semibold">{{ torneo.estado }}</span>
            </p>
            <p class="text-gray-600 mt-1">
              📊 {{ torneo.total_categorias }} categorías
            </p>
          </div>

          <div class="text-right">
            <span :class="badgeClass(torneo.estado)" class="badge">
              {{ torneo.estado.toUpperCase() }}
            </span>
          </div>
        </div>

        <!-- Categorías del torneo -->
        <div class="border-t pt-4">
          <p class="font-semibold mb-2">Categorías:</p>
          <div class="flex flex-wrap gap-2">
            <span
              v-for="cat in torneo.categorias || []"
              :key="cat.categoria_id"
              class="badge-info"
            >
              {{ cat.nombre }}
              <span v-if="!cat.llaves_generadas" class="ml-2 text-xs">⚠ Sin llaves</span>
            </span>
          </div>
        </div>

        <!-- Acciones -->
        <div class="flex gap-2 mt-4">
          <router-link
            :to="`/admin/torneo/${torneo.id}`"
            class="btn-primary"
          >
            Gestionar
          </router-link>

          <button
            v-if="torneo.estado === 'planeado'"
            @click="cambiarEstado(torneo.id, 'en_curso')"
            class="btn-secondary"
          >
            Iniciar
          </button>

          <button
            v-if="torneo.estado === 'en_curso'"
            @click="cambiarEstado(torneo.id, 'finalizado')"
            class="btn-secondary"
          >
            Finalizar
          </button>

          <button
            @click="eliminarTorneo(torneo.id, torneo.nombre)"
            class="btn-danger text-sm px-4 py-2"
          >
            🗑️ Eliminar
          </button>
        </div>
      </div>
    </div>

    <!-- Alertas -->
    <Alert
      v-if="alert.show"
      :type="alert.type"
      :message="alert.message"
      :duration="3000"
    />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAPI } from '../../composables/useAPI'
import { useAuth } from '../../composables/useAuth'
import Modal from '../../components/Modal.vue'
import Input from '../../components/Input.vue'
import Loading from '../../components/Loading.vue'
import Alert from '../../components/Alert.vue'

const api = useAPI()
const router = useRouter()
const { logoutAdmin } = useAuth()
const torneos = ref([])
const categorias = ref([])
const loading = ref(false)
const showCreateModal = ref(false)

const form = ref({
  nombre: '',
  fecha: '',
  categorias: []
})

const alert = ref({
  show: false,
  type: 'info',
  message: ''
})

const formatDate = (date) => {
  return new Date(date).toLocaleDateString('es-CO', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}

const badgeClass = (estado) => {
  const clases = {
    planeado: 'badge-info',
    en_curso: 'badge-warning',
    finalizado: 'badge-success'
  }
  return clases[estado] || 'badge-info'
}

const crearTorneo = async () => {
  try {
    if (!form.value.nombre || !form.value.fecha || form.value.categorias.length === 0) {
      showAlert('error', 'Por favor completa todos los campos')
      return
    }

    loading.value = true
    await api.createTorneo(form.value.nombre, form.value.fecha, form.value.categorias)
    showAlert('success', 'Torneo creado exitosamente')
    showCreateModal.value = false
    form.value = { nombre: '', fecha: '', categorias: [] }
    await cargarTorneos()
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    loading.value = false
  }
}

const cambiarEstado = async (id, estado) => {
  try {
    await api.updateTorneo(id, estado)
    showAlert('success', 'Estado actualizado')
    await cargarTorneos()
  } catch (err) {
    showAlert('error', err.message)
  }
}

const eliminarTorneo = async (id, nombre) => {
  if (confirm(`¿Estás seguro de que deseas eliminar el torneo "${nombre}"? Esta acción no se puede deshacer.`)) {
    try {
      loading.value = true
      console.log('🗑️ Eliminando torneo ID:', id)
      await api.deleteTorneo(id)
      console.log('✅ Torneo eliminado exitosamente')
      showAlert('success', 'Torneo eliminado exitosamente')
      await cargarTorneos()
    } catch (err) {
      console.error('❌ Error eliminando torneo:', err)
      showAlert('error', 'Error al eliminar: ' + err.message)
    } finally {
      loading.value = false
    }
  }
}

const cargarTorneos = async () => {
  try {
    loading.value = true
    torneos.value = await api.getTorneos()
  } catch (err) {
    showAlert('error', err.message)
  } finally {
    loading.value = false
  }
}

const cargarCategorias = async () => {
  try {
    categorias.value = await api.getCategorias()
    console.log('Categorías cargadas:', categorias.value)
  } catch (err) {
    console.error('Error cargando categorías:', err)
    showAlert('error', 'Error cargando categorías: ' + err.message)
  }
}

const showAlert = (type, message) => {
  alert.value = { show: true, type, message }
}

const cerrarSesionAdmin = () => {
  if (confirm('¿Estás seguro de que deseas cerrar sesión?')) {
    logoutAdmin()
    router.push('/')
  }
}

onMounted(async () => {
  await cargarTorneos()
  await cargarCategorias()
})
</script>
